import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LiveKitRoom,
  VideoConference,
  GridLayout,
  ParticipantTile,
  useTracks,
  RoomAudioRenderer,
  ControlBar,
  useRoomContext,
  useParticipants,
  useLocalParticipant,
} from '@livekit/components-react';
import '@livekit/components-styles';
import { Track } from 'livekit-client';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import {
  MessageSquare,
  Users,
  PhoneOff,
  Send,
  Smile,
  Copy,
  Video,
  VideoOff,
  Mic,
  MicOff,
  Circle,
  Share2,
  Mail,
  Check,
  FileText,
  Bot,
  Sparkles,
  Hand
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

// AI Components
import TranscriptionPanel from '../components/meeting/TranscriptionPanel';
import AICopilotPanel from '../components/meeting/AICopilotPanel';
import MeetingSummaryModal from '../components/meeting/MeetingSummaryModal';
import ParticipantsPanel from '../components/meeting/ParticipantsPanel';
import { useTranscription } from '../hooks/useTranscription';
import { useAIAssistant } from '../hooks/useAIAssistant';

const REACTIONS = ['👍', '👏', '❤️', '😂', '🎉', '🔥', '👋', '💯'];
const LIVEKIT_URL = process.env.REACT_APP_LIVEKIT_URL || 'wss://meet-2wx5nfq3.livekit.cloud';

// Chat panel component
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';

const ChatPanel = ({ roomId, user, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (!roomId) return;

    // Real-time listener
    const q = query(
      collection(db, 'messages'),
      where('room_id', '==', roomId)
      // orderBy removed to avoid index requirement
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map(doc => ({
        message_id: doc.id,
        ...doc.data()
      }));

      // Client-side sorting
      msgs.sort((a, b) => {
        const timeA = a.created_at?.toMillis ? a.created_at.toMillis() : 0;
        const timeB = b.created_at?.toMillis ? b.created_at.toMillis() : 0;
        return timeA - timeB;
      });

      setMessages(msgs);
    }, (error) => {
      console.error("Chat Error:", error);
      if (error.code === 'failed-precondition') {
        toast.error("Chat requires an index. Check browser console for the link to create it.");
      }
    });

    return () => unsubscribe();
  }, [roomId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    const content = newMessage.trim();
    if (!content) return;

    // Clear immediately so the input feels responsive; restore it if the
    // send actually fails so the user doesn't lose what they typed.
    setNewMessage('');

    try {
      // Sent through the backend (which writes via Firebase Admin SDK) rather
      // than directly from the browser — direct client writes to Firestore
      // are subject to security rules and were silently failing.
      await api.post(`/rooms/${roomId}/chat`, { content });
    } catch (error) {
      console.error("Send Error:", error);
      toast.error('Failed to send message');
      setNewMessage(content);
    }
  };

  return (
    <motion.div
      initial={{ width: 0, opacity: 0 }}
      animate={{ width: 'auto', opacity: 1 }}
      exit={{ width: 0, opacity: 0 }}
      className="chat-panel-mobile md:w-[320px] h-full max-h-[calc(100dvh-140px)] bg-void-paper border-l border-white/5 flex flex-col overflow-hidden z-20"
    >
      <div className="p-4 border-b border-white/5 flex items-center justify-between">
        <h3 className="font-heading font-semibold text-white">Chat</h3>
        <button onClick={onClose} className="text-grey-400 hover:text-white text-xl">×</button>
      </div>

      <div className="flex-1 min-h-0">
        <ScrollArea className="h-full p-4">
          {messages.length === 0 ? (
            <div className="text-center text-grey-400 py-8">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No messages yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg) => (
                <div
                  key={msg.message_id}
                  className={`flex gap-3 ${msg.user_id === user?.id ? 'flex-row-reverse' : ''}`}
                >
                  <Avatar className="w-8 h-8 flex-shrink-0">
                    <AvatarImage src={msg.user_picture} />
                    <AvatarFallback className="bg-neon-cyan/20 text-neon-cyan text-xs">
                      {msg.user_name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className={`max-w-[200px] ${msg.user_id === user?.id ? 'text-right' : ''}`}>
                    <p className="text-xs text-grey-400 mb-1">{msg.user_name}</p>
                    <div className={`rounded-xl px-3 py-2 ${msg.user_id === user?.id
                      ? 'bg-neon-cyan text-black'
                      : 'bg-white/5 text-grey-100'
                      }`}>
                      <p className="text-sm break-words">{msg.content}</p>
                    </div>
                  </div>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
          )}
        </ScrollArea>
      </div>

      <div className="p-4 border-t border-white/5">
        <div className="flex gap-2">
          <Input
            placeholder="Type a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
            className="input-neon text-sm h-10"
          />
          <Button onClick={sendMessage} className="bg-neon-cyan text-black hover:bg-neon-cyan/80 h-10 px-3">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

// Meeting room inner component with LiveKit hooks
const MeetingRoomInner = ({ room, user, onLeave }) => {
  const navigate = useNavigate();
  const roomContext = useRoomContext();
  const participants = useParticipants();
  const { localParticipant } = useLocalParticipant();

  const [showChat, setShowChat] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [reactions, setReactions] = useState([]);
  // Raised hands, keyed by participant identity: { [identity]: { name, raisedAt } }
  const [raisedHands, setRaisedHands] = useState({});

  // AI Feature States
  const [showTranscription, setShowTranscription] = useState(false);
  const [showAICopilot, setShowAICopilot] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);

  // Open only one side panel at a time (Google-Meet style). Mutually
  // exclusive so panels don't stack/overlap, especially on mobile.
  const openPanel = useCallback((panel) => {
    setShowChat(panel === 'chat');
    setShowParticipants(panel === 'participants');
    setShowTranscription(panel === 'transcription');
    setShowAICopilot(panel === 'ai');
  }, []);

  // AI Hooks
  const transcription = useTranscription(room?.room_id, user);
  const aiAssistant = useAIAssistant(room?.room_id);

  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: false },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false }
  );

  // Helper to display a reaction animation
  const displayReaction = useCallback((emoji) => {
    const reactionId = Date.now() + Math.random();
    setReactions(prev => [...prev, { id: reactionId, emoji, x: Math.random() * 80 + 10 }]);
    setTimeout(() => {
      setReactions(prev => prev.filter(r => r.id !== reactionId));
    }, 3000);
  }, []);

  // Listen for reactions from other participants via DataChannel
  useEffect(() => {
    if (!roomContext) return;

    const handleDataReceived = (payload, participant) => {
      try {
        const decoder = new TextDecoder();
        const message = JSON.parse(decoder.decode(payload));
        if (message.type === 'reaction' && message.emoji) {
          displayReaction(message.emoji);
        } else if (message.type === 'hand' && message.identity) {
          setRaisedHands((prev) => {
            if (message.raised) {
              return { ...prev, [message.identity]: { name: message.name, raisedAt: Date.now() } };
            }
            const next = { ...prev };
            delete next[message.identity];
            return next;
          });
          if (message.raised) {
            toast(`✋ ${message.name} raised their hand`);
          }
        }
      } catch (e) {
        console.error('Failed to parse data message:', e);
      }
    };

    // Clear a participant's raised hand if they leave while it's still up.
    const handleParticipantDisconnected = (participant) => {
      setRaisedHands((prev) => {
        if (!prev[participant.identity]) return prev;
        const next = { ...prev };
        delete next[participant.identity];
        return next;
      });
    };

    roomContext.on('dataReceived', handleDataReceived);
    roomContext.on('participantDisconnected', handleParticipantDisconnected);
    return () => {
      roomContext.off('dataReceived', handleDataReceived);
      roomContext.off('participantDisconnected', handleParticipantDisconnected);
    };
  }, [roomContext, displayReaction]);

  // Send reaction via DataChannel to all participants
  const sendReaction = async (emoji) => {
    try {
      // Broadcast to other participants via LiveKit DataChannel
      if (localParticipant) {
        const encoder = new TextEncoder();
        const data = encoder.encode(JSON.stringify({ type: 'reaction', emoji }));
        await localParticipant.publishData(data, { reliable: true });
      }
      // Show locally as well
      displayReaction(emoji);
    } catch (error) {
      console.error('Failed to send reaction:', error);
    }
  };

  const isHandRaised = Boolean(localParticipant && raisedHands[localParticipant.identity]);

  // Toggle the local participant's raised-hand state and broadcast it so
  // everyone (and the host in particular) sees a persistent indicator —
  // unlike reactions, this stays up until explicitly lowered.
  const toggleRaiseHand = useCallback(async () => {
    if (!localParticipant) return;
    const raising = !raisedHands[localParticipant.identity];
    const name = user?.name || user?.email?.split('@')[0] || 'Someone';

    setRaisedHands((prev) => {
      if (raising) {
        return { ...prev, [localParticipant.identity]: { name, raisedAt: Date.now() } };
      }
      const next = { ...prev };
      delete next[localParticipant.identity];
      return next;
    });

    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(JSON.stringify({
        type: 'hand',
        identity: localParticipant.identity,
        name,
        raised: raising,
      }));
      await localParticipant.publishData(data, { reliable: true });
    } catch (error) {
      console.error('Failed to broadcast raised hand:', error);
    }
  }, [localParticipant, raisedHands, user]);

  const raisedHandsList = Object.entries(raisedHands)
    .map(([identity, info]) => ({ identity, ...info }))
    .sort((a, b) => a.raisedAt - b.raisedAt);

  // Best-effort: generate & persist the AI summary while the transcript is
  // still in memory on the backend, so it's available later as "AI Notes"
  // on the dashboard. Silently ignores the "no transcript" case.
  const persistMeetingNotes = useCallback(async () => {
    try {
      await aiAssistant.generateSummary();
    } catch (e) {
      // No transcript / AI unavailable — notes simply won't be saved.
    }
  }, [aiAssistant]);

  const handleLeaveMeeting = useCallback(async () => {
    await persistMeetingNotes();
    try {
      if (roomContext && roomContext.state !== 'disconnected') {
        await roomContext.disconnect();
      }
    } catch (e) {
      console.error('Error disconnecting:', e);
    }
    // Logic continues in onDisconnected prop of LiveKitRoom
  }, [roomContext, persistMeetingNotes]);

  const handleEndMeetingForAll = useCallback(async () => {
    await persistMeetingNotes();
    try {
      // First disconnect self
      if (roomContext && roomContext.state !== 'disconnected') {
        await roomContext.disconnect();
      }
      // Then call backend to forcefully end it for everyone
      await api.delete(`/rooms/${room?.room_id}`);
      toast.success('Meeting ended for everyone');
    } catch (error) {
      console.error('Failed to end meeting for all:', error);
      toast.error('Failed to forcefully end meeting');
    }
  }, [roomContext, room?.room_id, persistMeetingNotes]);

  const copyMeetingLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Meeting link copied!');
  };

  return (
    <div className="meeting-room-container h-screen overflow-hidden bg-void flex flex-col" data-testid="meeting-room">
      {/* Floating Reactions */}
      <AnimatePresence>
        {reactions.map((reaction) => (
          <motion.div
            key={reaction.id}
            className="fixed text-4xl pointer-events-none z-50"
            initial={{ bottom: 100, left: `${reaction.x}%`, opacity: 1, scale: 0.5 }}
            animate={{ bottom: '80%', opacity: 0, scale: 1.5 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 3, ease: 'easeOut' }}
          >
            {reaction.emoji}
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Raised Hands Queue */}
      <AnimatePresence>
        {raisedHandsList.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="fixed top-16 md:top-20 left-1/2 -translate-x-1/2 z-40 bg-void-paper/95 backdrop-blur-xl border border-amber-400/30 rounded-xl px-4 py-2 shadow-lg max-w-[90vw]"
          >
            <div className="flex items-center gap-2 text-sm text-amber-300">
              <Hand className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">
                {raisedHandsList.map((h) => h.name).join(', ')}
                {raisedHandsList.length === 1 ? ' raised a hand' : ' raised their hands'}
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Meeting Header */}
      <header className="meeting-header bg-void-paper/50 backdrop-blur-xl border-b border-white/5 px-3 md:px-4 py-2 md:py-3 flex items-center justify-between z-10">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="RevealIQ Meet" className="logo-img w-8 h-8 md:w-12 md:h-12 object-contain" />
            <span className="font-heading font-semibold text-white hidden md:block">RevealIQ Meet</span>
          </div>
          <div className="h-6 w-px bg-white/10 hidden md:block" />
          <h1 className="font-heading font-medium text-white truncate max-w-[200px] md:max-w-none">
            {room?.name || 'Meeting'}
          </h1>
          <div className="flex items-center gap-1.5 md:gap-2 bg-green-500/20 text-green-400 px-2 md:px-3 py-1 rounded-full text-xs md:text-sm">
            <Circle className="w-3 h-3 fill-green-400" />
            Live
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Share Dialog */}
          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="text-grey-400 hover:text-white hover:bg-white/5"
              >
                <Share2 className="w-4 h-4 mr-2" />
                <span className="hidden md:inline">Share</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-void-paper border border-white/10">
              <DialogHeader>
                <DialogTitle className="text-white font-heading">Share Meeting</DialogTitle>
                <DialogDescription className="text-grey-400 text-sm">
                  Invite others to join this meeting by sharing the link below.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {/* Meeting Link */}
                <div className="flex items-center gap-2 bg-white/5 rounded-lg p-3">
                  <Input
                    value={window.location.href}
                    readOnly
                    className="bg-transparent border-none text-grey-100 text-sm flex-1"
                  />
                  <Button
                    size="sm"
                    onClick={copyMeetingLink}
                    className="bg-neon-cyan text-black hover:bg-neon-cyan/80"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>

                {/* Share Options */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => {
                      const emailSubject = encodeURIComponent(`You're invited to join: ${room?.name || 'Meeting'}`);
                      const emailBody = encodeURIComponent(`Hi,\n\nI'd like to invite you to join my video meeting.\n\nMeeting: ${room?.name || 'Meeting'}\nJoin now: ${window.location.href}\n\nSee you there!`);
                      window.open(`mailto:?subject=${emailSubject}&body=${emailBody}`, '_blank');
                    }}
                    className="flex items-center gap-3 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Email</p>
                      <p className="text-grey-400 text-xs">Send invite via email</p>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      const whatsappMsg = encodeURIComponent(`Hey! Join my video meeting "${room?.name || 'Meeting'}" on RevealIQ Meet:\n\n${window.location.href}`);
                      window.open(`https://wa.me/?text=${whatsappMsg}`, '_blank');
                    }}
                    className="flex items-center gap-3 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-400" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-white font-medium">WhatsApp</p>
                      <p className="text-grey-400 text-xs">Share via WhatsApp</p>
                    </div>
                  </button>
                </div>

                {/* Meeting Info */}
                <div className="text-center pt-4 border-t border-white/5">
                  <p className="text-grey-400 text-sm">Meeting ID: <span className="text-white">{room?.room_id}</span></p>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <button
            onClick={() => openPanel(showParticipants ? null : 'participants')}
            className="flex items-center gap-1 text-grey-400 hover:text-white text-sm transition-colors"
            title="View people"
          >
            <Users className="w-4 h-4" />
            <span>{participants.length}</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Video Grid */}
        <div className="video-grid-container flex-1 min-h-0 p-2 md:p-4">
          <GridLayout tracks={tracks} style={{ height: '100%' }}>
            <ParticipantTile />
          </GridLayout>
        </div>

        {/* Chat Panel */}
        <AnimatePresence>
          {showChat && (
            <ChatPanel
              roomId={room?.room_id}
              user={user}
              onClose={() => setShowChat(false)}
            />
          )}
        </AnimatePresence>

        {/* Participants Panel */}
        <AnimatePresence>
          {showParticipants && (
            <ParticipantsPanel
              participants={participants}
              hostId={room?.host_id}
              raisedHands={raisedHands}
              onClose={() => setShowParticipants(false)}
            />
          )}
        </AnimatePresence>

        {/* Transcription Panel */}
        <AnimatePresence>
          {showTranscription && (
            <TranscriptionPanel
              isTranscribing={transcription.isTranscribing}
              isConnecting={transcription.isConnecting}
              transcript={transcription.transcript}
              onToggle={transcription.toggleTranscription}
              onClose={() => setShowTranscription(false)}
              error={transcription.error}
            />
          )}
        </AnimatePresence>

        {/* AI Copilot Panel */}
        <AnimatePresence>
          {showAICopilot && (
            <AICopilotPanel
              chatHistory={aiAssistant.chatHistory}
              isLoading={aiAssistant.isLoading}
              onSendMessage={aiAssistant.askAssistant}
              onGenerateSummary={async () => {
                await aiAssistant.generateSummary();
                setShowSummaryModal(true);
              }}
              onGetInsights={aiAssistant.getInsights}
              insights={aiAssistant.insights}
              onClose={() => setShowAICopilot(false)}
              error={aiAssistant.error}
            />
          )}
        </AnimatePresence>
      </div>

      {/* Meeting Summary Modal */}
      <MeetingSummaryModal
        isOpen={showSummaryModal}
        onClose={() => setShowSummaryModal(false)}
        summary={aiAssistant.summary}
        isLoading={aiAssistant.isLoading}
        onRegenerate={aiAssistant.generateSummary}
        roomName={room?.name}
      />

      {/* Control Bar - Custom wrapper around LiveKit controls */}
      <div className="meeting-control-bar bg-void-paper/80 backdrop-blur-xl border-t border-white/5 p-3 md:p-4">
        <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4">
          <ControlBar
            variation="minimal"
            controls={{
              camera: true,
              microphone: true,
              screenShare: true,
              leave: false,
              chat: false
            }}
          />

          {/* Reactions */}
          <Sheet>
            <SheetTrigger asChild>
              <button className="w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
                <Smile className="w-5 h-5 text-white" />
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="bg-void-paper border-white/10">
              <SheetHeader>
                <SheetTitle className="text-white">Reactions</SheetTitle>
              </SheetHeader>
              <div className="flex flex-wrap gap-3 mt-4 justify-center">
                {REACTIONS.map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => sendReaction(emoji)}
                    className="w-11 h-11 md:w-12 md:h-12 text-2xl rounded-xl bg-white/5 hover:bg-white/10 transition-colors flex items-center justify-center"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </SheetContent>
          </Sheet>

          {/* Raise Hand Toggle */}
          <button
            onClick={toggleRaiseHand}
            className={`relative w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-colors ${isHandRaised ? 'bg-amber-400 text-black' : 'bg-white/10 hover:bg-white/20 text-white'
              }`}
            title={isHandRaised ? 'Lower hand' : 'Raise hand'}
          >
            <Hand className="w-5 h-5" />
            {raisedHandsList.length > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-amber-400 text-black text-[10px] font-bold rounded-full flex items-center justify-center">
                {raisedHandsList.length}
              </span>
            )}
          </button>

          {/* Participants Toggle */}
          <button
            onClick={() => openPanel(showParticipants ? null : 'participants')}
            className={`relative w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-colors ${showParticipants ? 'bg-neon-cyan text-black' : 'bg-white/10 hover:bg-white/20 text-white'
              }`}
            title="People"
          >
            <Users className="w-5 h-5" />
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-neon-cyan text-black text-[10px] font-bold rounded-full flex items-center justify-center">
              {participants.length}
            </span>
          </button>

          {/* Chat Toggle */}
          <button
            onClick={() => openPanel(showChat ? null : 'chat')}
            className={`w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-colors ${showChat ? 'bg-neon-cyan text-black' : 'bg-white/10 hover:bg-white/20 text-white'
              }`}
            title="Chat"
          >
            <MessageSquare className="w-5 h-5" />
          </button>

          {/* Transcription Toggle */}
          <button
            onClick={() => openPanel(showTranscription ? null : 'transcription')}
            className={`w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-colors relative ${showTranscription ? 'bg-neon-cyan text-black' : 'bg-white/10 hover:bg-white/20 text-white'
              }`}
            title="Transcription"
          >
            <FileText className="w-5 h-5" />
            {transcription.isTranscribing && (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse" />
            )}
          </button>

          {/* AI Copilot Toggle */}
          <button
            onClick={() => openPanel(showAICopilot ? null : 'ai')}
            className={`w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-colors ${showAICopilot ? 'bg-gradient-to-r from-neon-purple to-neon-cyan text-white' : 'bg-white/10 hover:bg-white/20 text-white'
              }`}
            title="AI Copilot"
          >
            <Bot className="w-5 h-5" />
          </button>

          {/* Leave Meeting / End Meeting */}
          {user?.id === room?.host_id ? (
            <Dialog>
              <DialogTrigger asChild>
                <button
                  className="w-11 h-11 md:w-12 md:h-12 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors"
                >
                  <PhoneOff className="w-5 h-5 text-white" />
                </button>
              </DialogTrigger>
              <DialogContent className="bg-void-paper border border-white/10">
                <DialogHeader>
                  <DialogTitle className="text-white font-heading">Leave Meeting</DialogTitle>
                  <DialogDescription className="text-grey-400 text-sm">
                    You are the host. Do you want to just leave, or end the meeting for everyone?
                  </DialogDescription>
                </DialogHeader>
                <div className="flex justify-end gap-3 mt-4">
                  <Button variant="ghost" onClick={handleLeaveMeeting} className="text-grey-400 hover:text-white">
                    Just Leave
                  </Button>
                  <Button onClick={handleEndMeetingForAll} className="bg-red-500 text-white hover:bg-red-600">
                    End for All
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          ) : (
            <button
              onClick={handleLeaveMeeting}
              className="w-11 h-11 md:w-12 md:h-12 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors"
            >
              <PhoneOff className="w-5 h-5 text-white" />
            </button>
          )}

        </div>
      </div>

      {/* Audio Renderer */}
      <RoomAudioRenderer />
    </div>
  );
};

// Custom PreJoin Screen — Google Meet Style
const CustomPreJoin = ({ user, onJoin }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [ready, setReady] = useState(false);

  const userName = user?.displayName || user?.email?.split('@')[0] || 'Guest';
  const userPhoto = user?.photoURL || '';
  const userInitials = userName.slice(0, 2).toUpperCase();

  // Start camera preview
  useEffect(() => {
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        setReady(true);
      } catch (err) {
        console.warn('Could not access media devices:', err);
        setReady(true);
      }
    };
    startCamera();

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  // Toggle video track
  useEffect(() => {
    if (streamRef.current) {
      streamRef.current.getVideoTracks().forEach(t => { t.enabled = videoEnabled; });
    }
  }, [videoEnabled]);

  // Toggle audio track
  useEffect(() => {
    if (streamRef.current) {
      streamRef.current.getAudioTracks().forEach(t => { t.enabled = audioEnabled; });
    }
  }, [audioEnabled]);

  const handleJoin = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    onJoin({ videoEnabled, audioEnabled });
  };

  return (
    <div className="min-h-screen bg-void flex items-center justify-center p-4">
      {/* Background Glow */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-neon-cyan/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-neon-purple/5 rounded-full blur-[120px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-4xl"
      >
        <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-8 text-center">
          Ready to join?
        </h2>

        <div className="flex flex-col md:flex-row gap-6 items-stretch">
          {/* Camera Preview */}
          <div className="flex-1 relative bg-black rounded-3xl overflow-hidden border border-white/10 shadow-2xl min-h-[300px] md:min-h-[400px]">
            {videoEnabled ? (
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
                style={{ transform: 'scaleX(-1)' }}
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center bg-void-paper">
                {userPhoto ? (
                  <img src={userPhoto} alt={userName} className="w-24 h-24 rounded-full border-2 border-white/20" />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-neon-cyan to-neon-purple flex items-center justify-center text-3xl font-bold text-white">
                    {userInitials}
                  </div>
                )}
                <p className="mt-4 text-grey-400 text-sm">Camera is off</p>
              </div>
            )}

            {/* Floating Controls Over Video */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-3">
              <button
                onClick={() => setAudioEnabled(!audioEnabled)}
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 backdrop-blur-xl border ${
                  audioEnabled
                    ? 'bg-white/15 border-white/20 text-white hover:bg-white/25'
                    : 'bg-red-500/80 border-red-400/50 text-white hover:bg-red-500'
                }`}
              >
                {audioEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
              </button>
              <button
                onClick={() => setVideoEnabled(!videoEnabled)}
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 backdrop-blur-xl border ${
                  videoEnabled
                    ? 'bg-white/15 border-white/20 text-white hover:bg-white/25'
                    : 'bg-red-500/80 border-red-400/50 text-white hover:bg-red-500'
                }`}
              >
                {videoEnabled ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Right Panel — User Info + Join */}
          <div className="md:w-[300px] flex flex-col items-center justify-center gap-6 bg-void-paper/30 backdrop-blur-xl border border-white/10 rounded-3xl p-8">
            {/* User Avatar */}
            <div className="relative">
              {userPhoto ? (
                <img
                  src={userPhoto}
                  alt={userName}
                  className="w-20 h-20 rounded-full border-2 border-neon-cyan/50 shadow-[0_0_20px_rgba(0,240,255,0.15)]"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-neon-cyan to-neon-purple flex items-center justify-center text-2xl font-bold text-white shadow-[0_0_20px_rgba(0,240,255,0.15)]">
                  {userInitials}
                </div>
              )}
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-void-paper" />
            </div>

            {/* User Name */}
            <div className="text-center">
              <p className="text-white font-heading font-semibold text-lg">{userName}</p>
              <p className="text-grey-400 text-sm mt-1">{user?.email}</p>
            </div>

            {/* Status Indicators */}
            <div className="flex gap-3 text-sm">
              <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${
                audioEnabled ? 'border-green-500/30 text-green-400 bg-green-500/10' : 'border-red-500/30 text-red-400 bg-red-500/10'
              }`}>
                {audioEnabled ? <Mic className="w-3.5 h-3.5" /> : <MicOff className="w-3.5 h-3.5" />}
                {audioEnabled ? 'Mic on' : 'Mic off'}
              </span>
              <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${
                videoEnabled ? 'border-green-500/30 text-green-400 bg-green-500/10' : 'border-red-500/30 text-red-400 bg-red-500/10'
              }`}>
                {videoEnabled ? <Video className="w-3.5 h-3.5" /> : <VideoOff className="w-3.5 h-3.5" />}
                {videoEnabled ? 'Cam on' : 'Cam off'}
              </span>
            </div>

            {/* Join Button */}
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleJoin}
              className="w-full py-4 bg-neon-cyan text-black font-semibold text-lg rounded-2xl transition-all duration-300 shadow-[0_0_25px_rgba(0,240,255,0.3)] hover:shadow-[0_0_40px_rgba(0,240,255,0.5)] hover:bg-neon-cyan/90"
            >
              Join Meeting
            </motion.button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Main component
const MeetingRoom = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [room, setRoom] = useState(null);
  const [token, setToken] = useState(null);
  const [liveKitUrl, setLiveKitUrl] = useState(LIVEKIT_URL);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pre-join State
  const [preJoinComplete, setPreJoinComplete] = useState(false);
  const [preJoinChoices, setPreJoinChoices] = useState(null);
  
  const joinedRef = useRef(false);

  useEffect(() => {
    const joinRoom = async () => {
      if (joinedRef.current) return;
      joinedRef.current = true;

      try {
        const response = await api.post(`/rooms/${roomId}/join`);
        if (response.data.livekit_url) {
          // Ensure it's wss if possible, or trust the backend's https
          setLiveKitUrl(response.data.livekit_url);
        }

        const receivedToken = response.data.token;
        if (receivedToken && receivedToken.startsWith('mock_token')) {
          console.warn("Received mock token! LiveKit keys may be missing on backend.");
          toast.warning("Backend LiveKit not configured (Mock Token)");
        }
        setRoom(response.data.room);
        setToken(receivedToken);
        setLoading(false);
      } catch (error) {
        console.error('Failed to join meeting:', error);
        setError('Failed to join meeting. Please try again.');
        toast.error('Failed to join meeting');
        setLoading(false);
        joinedRef.current = false;
      }
    };

    if (user && preJoinComplete) {
      joinRoom();
    }
  }, [roomId, user, preJoinComplete]);

  const handleLeave = async () => {
    try {
      await api.post(`/rooms/${roomId}/leave`);
    } catch (error) {
      console.error('Failed to leave room:', error);
    }
  };

  // Show PreJoin screen FIRST, before any loading/error states
  if (!preJoinComplete) {
    return (
      <CustomPreJoin
        user={user}
        onJoin={(choices) => {
          setPreJoinChoices(choices);
          setPreJoinComplete(true);
        }}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-neon-cyan border-t-transparent rounded-full animate-spin" />
          <p className="text-grey-100 font-heading text-xl">Joining meeting...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-center">
          <p className="text-red-400 font-heading text-xl">{error}</p>
          <Button onClick={() => navigate('/dashboard')} className="bg-neon-cyan text-black">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (!token) {
    return null;
  }

  return (
    <LiveKitRoom
      token={token}
      serverUrl={liveKitUrl}
      connect={true}
      video={preJoinChoices?.videoEnabled ?? true}
      audio={preJoinChoices?.audioEnabled ?? true}
      data-lk-theme="default"
      style={{ height: '100vh' }}
      onDisconnected={(reason) => {
        console.log("Disconnected from room, reason:", reason);
        toast.info(`Disconnected: ${reason || 'Unknown'}`);
        handleLeave();
        navigate('/dashboard');
      }}
      onError={(err) => {
        console.error("LiveKit Room Error:", err);
        toast.error(`Error: ${err.message || 'Unknown'}`);
      }}
    >
      <MeetingRoomInner room={room} user={user} onLeave={handleLeave} />
    </LiveKitRoom>
  );
};

export default MeetingRoom;
